import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, Loader2, XCircle, X } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import {
  cancelBackgroundJob,
  kickQueuedJobs,
  kickStaleQueuedJobs,
  listBackgroundJobs,
  subscribeBackgroundJobs,
  type BackgroundJob,
} from '../lib/backgroundJobs';

function statusLabel(status: BackgroundJob['status'], job: BackgroundJob): string {
  if (status === 'queued') {
    const ageMs = Date.now() - new Date(job.created_at).getTime();
    if (ageMs > 60_000) return 'Waiting to start';
    return 'Queued';
  }
  switch (status) {
    case 'running':
      return 'Running';
    case 'completed':
      return 'Done';
    case 'failed':
      return 'Failed';
    case 'cancelled':
      return 'Cancelled';
    default:
      return status;
  }
}

function progressText(job: BackgroundJob): string {
  if (job.total_count > 0) {
    return `${job.done_count} / ${job.total_count}`;
  }
  if (job.status === 'queued') return 'Starting…';
  if (job.status === 'running') return `${job.done_count} done`;
  return '';
}

export const BackgroundJobsDropdown: React.FC = () => {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;

  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const portalContentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const load = useCallback(async () => {
    if (!businessId) {
      setJobs([]);
      return;
    }
    try {
      const rows = await listBackgroundJobs(businessId, { limit: 15 });
      setJobs(rows);
      if (rows.some((j) => j.status === 'queued')) {
        void kickQueuedJobs(rows).then(async () => {
          try {
            const refreshed = await listBackgroundJobs(businessId, { limit: 15 });
            setJobs(refreshed);
          } catch {
            /* ignore refresh errors */
          }
        });
      }
    } catch (err) {
      console.error('[BackgroundJobsDropdown] load failed', err);
    }
  }, [businessId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!businessId) return;
    return subscribeBackgroundJobs(businessId, () => {
      void load();
      // Refresh Tasks UI when jobs finish creating todos
      window.dispatchEvent(
        new CustomEvent('todoItemsChanged', { detail: { businessId } })
      );
    });
  }, [businessId, load]);

  useEffect(() => {
    if (!businessId || jobs.length === 0) return;
    const interval = window.setInterval(() => {
      void kickStaleQueuedJobs(businessId, jobs).then(() => load());
    }, 15_000);
    return () => window.clearInterval(interval);
  }, [businessId, jobs, load]);

  useEffect(() => {
    if (!isOpen) return;
    void load();
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        right:
          window.innerWidth < 640
            ? Math.min(16, window.innerWidth - rect.right)
            : Math.max(16, window.innerWidth - rect.right),
      });
    }
  }, [isOpen, load]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideAnchor = dropdownRef.current?.contains(target) ?? false;
      const insidePortal = portalContentRef.current?.contains(target) ?? false;
      if (!insideAnchor && !insidePortal) setIsOpen(false);
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const activeJobs = jobs.filter((j) => j.status === 'queued' || j.status === 'running');
  const showSpinner = activeJobs.length > 0;

  const handleCancel = async (job: BackgroundJob) => {
    if (!businessId || cancellingId) return;
    setCancellingId(job.id);
    try {
      await cancelBackgroundJob(businessId, job.id);
      await load();
    } catch (err) {
      console.error('[BackgroundJobsDropdown] cancel failed', err);
    } finally {
      setCancellingId(null);
    }
  };

  // Always mounted so realtime keeps working; hide button when idle
  if (!businessId) return null;

  const panel = isOpen && dropdownPosition
    ? createPortal(
        <div
          ref={portalContentRef}
          className="fixed z-[100200] w-[min(100vw-2rem,360px)] rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden"
          style={{
            top: dropdownPosition.top,
            right: dropdownPosition.right,
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          }}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
            <div>
              <p className="text-sm font-semibold text-gray-900">Background tasks</p>
              <p className="text-xs text-gray-500">Bulk jobs keep running if you leave</p>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {jobs.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-gray-500">
                No background jobs yet
              </p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {jobs.map((job) => {
                  const active = job.status === 'queued' || job.status === 'running';
                  const progress = progressText(job);
                  return (
                    <li key={job.id} className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <span className="mt-0.5 shrink-0">
                          {active ? (
                            <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                          ) : job.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-400" />
                          )}
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-gray-900 leading-snug">
                            {job.title}
                          </p>
                          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
                            <span className="font-medium text-gray-600">
                              {statusLabel(job.status, job)}
                              {job.cancel_requested && active ? ' · cancelling…' : ''}
                            </span>
                            {progress ? <span>{progress}</span> : null}
                            {job.error_count > 0 ? (
                              <span className="text-amber-600">{job.error_count} errors</span>
                            ) : null}
                          </div>
                          {job.result_summary ? (
                            <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                              {job.result_summary}
                            </p>
                          ) : null}
                          {job.error ? (
                            <p className="mt-1 text-xs text-red-600 line-clamp-2">{job.error}</p>
                          ) : null}
                        </div>
                        {active && !job.cancel_requested ? (
                          <button
                            type="button"
                            onClick={() => void handleCancel(job)}
                            disabled={cancellingId === job.id}
                            className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        ) : null}
                      </div>
                      {active && job.total_count > 0 ? (
                        <div className="mt-2 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-blue-500 transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                Math.round((job.done_count / job.total_count) * 100)
                              )}%`,
                            }}
                          />
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div ref={dropdownRef} className="relative">
      {(showSpinner || isOpen) && (
        <button
          ref={buttonRef}
          type="button"
          onClick={() => setIsOpen((v) => !v)}
          className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800/50 transition-colors touch-manipulation"
          aria-label={
            showSpinner
              ? `Background tasks · ${activeJobs.length} running`
              : 'Background tasks'
          }
          title="Background tasks"
          style={{ WebkitTapHighlightColor: 'transparent' }}
        >
          <Loader2
            className={`h-5 w-5 ${showSpinner ? 'animate-spin text-blue-400' : ''}`}
          />
        </button>
      )}
      {panel}
    </div>
  );
};

export default BackgroundJobsDropdown;
